package org.matthewo.components

import emotion.react.css
import org.matthewo.Searcher
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import react.FC
import react.Key
import react.PropsWithClassName
import react.dom.aria.AriaAutoComplete
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.ul
import react.useEffect
import react.useId
import react.useMemo
import react.useState
import web.cssom.*
import web.dom.ElementId
import web.dom.document
import web.autofill.AutoFillBase
import web.autofill.off
import web.html.*
import web.scroll.ScrollIntoViewOptions
import web.scroll.ScrollLogicalPosition
import web.scroll.nearest

/** A team that can be searched for by its number or its name. */
data class TeamOption(val number: Int, val name: String)

/**
 * Finds the teams for what's typed: those whose number starts with it, by number, then those with a name like it.
 * Nothing typed finds every team.
 */
class TeamMatcher(teams: List<TeamOption>) {
    private val byNumber = teams.sortedBy { it.number }
    private val searcher = Searcher(teams) { it.name }

    fun matches(text: String): List<TeamOption> {
        val text = text.trim()
        val numberMatches = byNumber.filter { it.number.toString().startsWith(text) }
        val numbers = numberMatches.mapTo(mutableSetOf()) { it.number }
        val nameMatches = searcher.search(text).map { it.first }.filter { it.number !in numbers }
        return numberMatches + nameMatches
    }
}

external interface TeamSearchProps : PropsWithClassName {
    // null until they're known, and a typed number is taken as it is until then
    var teams: List<TeamOption>?
    var label: String
    var placeholder: String?
    var disabled: Boolean?

    /**
     * Whether the search holds the team it picked, showing its number while it isn't being searched, as a choice of
     * one team: nothing's chosen while it's searched, and leaving it picks its best match. Otherwise a picked team is
     * handed to [onSelect] and the search empties for another.
     */
    var keepsSelection: Boolean?

    // the team it holds, when it [keepsSelection]
    var selected: Int?
    var onSelect: (TeamOption?) -> Unit

    // when something's picked that matches no team, with what was typed
    var onNoMatch: ((String) -> Unit)?

    // a button beside the search that picks its best match, as enter does
    var submitLabel: String?
}

/**
 * A box that searches the teams as a number or name is typed, listing the matches below it to pick from with a click
 * or the arrow keys and enter. [TeamSearchProps.className] styles the box, and its `[role=listbox]` the list.
 */
val TeamSearch = FC<TeamSearchProps>("TeamSearch") { props ->
    val keepsSelection = props.keepsSelection == true
    var text by useState { props.selected?.toString() ?: "" }
    var focused by useState(false)
    var expanded by useState(false)
    // the match the arrow keys moved to; with none, the first is picked once something's typed
    var active by useState<Int>()
    val listId = useId()

    val matcher = useMemo(props.teams) { TeamMatcher(props.teams ?: []) }
    val matches = useMemo(matcher, text) { matcher.matches(text) }
    val activeIndex = (active ?: if (text.isNotBlank()) 0 else null)?.takeIf { it in matches.indices }
    val showing = expanded && matches.isNotEmpty()

    fun optionId(index: Int) = ElementId("$listId-$index")

    useEffect(focused, props.selected) {
        if (keepsSelection && !focused) text = props.selected?.toString() ?: ""
    }

    useEffect(activeIndex, showing) {
        if (showing && null != activeIndex) {
            document.getElementById(optionId(activeIndex))?.scrollIntoView(ScrollIntoViewOptions(block = ScrollLogicalPosition.nearest))
        }
    }

    fun best() = activeIndex?.let { matches[it] }
        ?: if (null == props.teams) text.trim().toIntOrNull()?.takeIf { it > 0 }?.let { TeamOption(it, "") } else null

    fun pick(team: TeamOption?) {
        expanded = false
        active = null
        if (keepsSelection) {
            text = team?.number?.toString() ?: ""
            props.onSelect(team)
        } else if (null != team) {
            text = ""
            props.onSelect(team)
        } else if (text.isNotBlank()) {
            props.onNoMatch?.invoke(text.trim())
        }
    }

    div {
        css {
            display = Display.flex
            gap = 0.5.em
        }

        div {
            css(props.className) {
                position = Position.relative
                flexGrow = number(1.0)
                minWidth = 0.px
            }

            input {
                type = InputType.text
                role = AriaRole.combobox
                ariaLabel = props.label
                ariaAutoComplete = AriaAutoComplete.list
                ariaExpanded = showing
                ariaControls = listId
                ariaActiveDescendant = if (showing) activeIndex?.let(::optionId) else null
                autoComplete = AutoFillBase.off
                placeholder = props.placeholder
                disabled = props.disabled == true
                value = text

                css {
                    display = Display.block
                    width = 100.pct
                    padding = Padding(0.4.em, 0.6.em)
                    margin = 0.px
                    border = None.none
                    boxSizing = BoxSizing.borderBox
                    fontSize = 1.em
                    backgroundColor = Color.transparent
                    color = Globals.inherit
                }

                onFocus = {
                    focused = true
                    expanded = true
                    if (keepsSelection) props.onSelect(null)
                }
                onBlur = {
                    focused = false
                    expanded = false
                    active = null
                    if (keepsSelection) pick(if (text.isBlank()) null else best())
                }
                onChange = {
                    text = it.target.value
                    expanded = true
                    active = null
                }
                onKeyDown = { event ->
                    when (event.key) {
                        "ArrowDown" -> {
                            event.preventDefault()
                            expanded = true
                            if (matches.isNotEmpty()) active = ((activeIndex ?: -1) + 1).coerceAtMost(matches.lastIndex)
                        }

                        "ArrowUp" -> {
                            event.preventDefault()
                            if (matches.isNotEmpty()) active = ((activeIndex ?: 0) - 1).coerceAtLeast(0)
                        }

                        "Enter" -> {
                            event.preventDefault()
                            pick(best())
                        }

                        // only closes the list, rather than the dialog it might be in
                        "Escape" -> if (showing) {
                            event.preventDefault()
                            event.stopPropagation()
                            expanded = false
                            active = null
                        }
                    }
                }
            }

            ul {
                id = listId
                role = AriaRole.listbox
                ariaLabel = props.label

                css {
                    if (!showing) display = None.none
                    position = Position.absolute
                    top = 100.pct
                    left = 0.px
                    right = 0.px
                    zIndex = integer(99)
                    margin = 0.px
                    padding = 0.px
                    listStyle = None.none
                    overflowX = Overflow.hidden
                    overflowY = Auto.auto
                    maxHeight = 50.vh
                    borderRadius = array(0.0.rem, 0.0.rem, 0.4.rem, 0.4.rem)
                    backgroundColor = Globals.inherit
                    color = Globals.inherit
                }

                if (showing) matches.forEachIndexed { i, team ->
                    li {
                        key = Key(team.number.toString())
                        id = optionId(i)
                        role = AriaRole.option
                        ariaSelected = i == activeIndex

                        css {
                            padding = 0.5.em
                            cursor = Cursor.pointer
                            backgroundColor = Globals.inherit
                            if (i == activeIndex) filter = brightness(0.8)
                            hover {
                                filter = brightness(0.8)
                            }
                        }

                        // keeps the focus in the search, which would otherwise leave it before the click
                        onMouseDown = { it.preventDefault() }
                        onClick = { pick(team) }

                        +if (team.name.isBlank()) "${team.number}" else "${team.number} - ${team.name}"
                    }
                }
            }
        }

        props.submitLabel?.let { label ->
            button {
                type = ButtonType.button
                disabled = props.disabled == true || text.isBlank()
                css {
                    flexShrink = number(0.0)
                    padding = Padding(0.4.em, 1.em)
                    border = None.none
                    borderRadius = 0.5.em
                    backgroundColor = TITLE_FONT_COLOR
                    color = FONT_COLOR
                    fontSize = 1.em
                    cursor = Cursor.pointer
                    disabled {
                        cursor = Cursor.default
                        opacity = number(0.5)
                    }
                }
                // keeps the match the arrow keys moved to, which leaving the search forgets
                onMouseDown = { it.preventDefault() }
                onClick = { pick(best()) }
                +label
            }
        }
    }
}
